import './theme/global.css';

import { StyledEngineProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { ErrorBoundary } from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router } from 'react-router-dom';

import { AppRouter } from './AppRouter';
import { ErrorFallbackPage } from './components/error/ErrorFallbackPage';
import { SettingsProvider } from './components/settings/context/settingsProvider';
import { defaultSettings } from './components/settings/settingsConfig';
import { Snackbar } from './components/snackbar';
import { EnvConfig } from './config/configMapping';
import { ApolloProviderInit } from './initialisers/apollo/ApolloProviderInit';
import { initSentry } from './initialisers/initSentry';
import { installPageExitTracking } from './lib/navigation/pageExit';
import { loadRoutesInWindow } from './lib/routes';
import {
  CHUNK_RELOAD_GUARD_CLEAR_MS,
  clearChunkReloadGuard,
  installChunkLoadReload,
  installVersionChecks,
  reloadOnceForChunkError,
} from './lib/version';
import { BasketProvider } from './pages/manual-booking/BasketProvider';
import { BookingForProvider } from './pages/manual-booking/BookingForProvider';
import { AuthKitProvider } from './providers/AuthKitProvider';
import { ConfigProvider } from './providers/ConfigProvider';
import { FeatureFlagProvider } from './providers/FeatureFlagProvider';
import { SocketProvider } from './providers/SocketProvider';
import { ViewerProvider } from './providers/ViewerProvider';
import { routes } from './routes';
import { themeConfig } from './theme/themeConfig';
import { ThemeProvider } from './theme/themeProvider';

/**
 * A lazy route whose chunk 404s is caught by the boundary below, so it never reaches the window
 * handlers that `installChunkLoadReload` registers. Give the boundary the same self-heal: a tab
 * left open across a deploy reloads onto the current chunk map instead of showing the error page.
 */
const selfHealStaleShell = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);

  reloadOnceForChunkError(message);
};

void (async () => {
  const envConfig = DO_NOT_USE_ENV_CONFIG as EnvConfig;

  initSentry(envConfig);

  const browserLocale = new Intl.DateTimeFormat().resolvedOptions().locale || 'en-AU';

  // Install safety nets and version update checks
  installPageExitTracking();
  installChunkLoadReload();
  // Re-arm the one-shot chunk reload once this build has proven it can run, so the next deploy can
  // self-heal this tab too.
  window.setTimeout(clearChunkReloadGuard, CHUNK_RELOAD_GUARD_CLEAR_MS);
  installVersionChecks(() => {
    // Reload on version change; keeps app consistent with backend and chunk map
    window.location.reload();
  });

  if (envConfig.environment !== 'production') {
    loadRoutesInWindow(routes);
  }

  // const container = document.getElementsByTagName('main');
  const container = document.getElementById('root');
  const root = createRoot(container!);

  root.render(
    <ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('scope', 'root');
      }}
      onError={selfHealStaleShell}
      fallback={ErrorFallbackPage}
      showDialog
    >
      <StyledEngineProvider injectFirst>
        <ErrorBoundary
          beforeCapture={(scope) => {
            scope.setTag('scope', 'App');
          }}
          onError={selfHealStaleShell}
          fallback={ErrorFallbackPage}
        >
          <ConfigProvider envConfig={envConfig}>
            <AuthKitProvider>
              <FeatureFlagProvider envConfig={envConfig}>
                <SocketProvider envConfig={envConfig}>
                  <ApolloProviderInit envConfig={envConfig}>
                    <Router>
                      <SettingsProvider defaultSettings={defaultSettings}>
                        <ThemeProvider
                          noSsr
                          defaultMode={themeConfig.defaultMode}
                          modeStorageKey={themeConfig.modeStorageKey}
                        >
                          <Helmet titleTemplate="%s - Altitude" defaultTitle="Altitude" />
                          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={browserLocale}>
                            <Snackbar />
                            <ViewerProvider>
                              <BasketProvider>
                                <BookingForProvider>
                                  <AppRouter />
                                </BookingForProvider>
                              </BasketProvider>
                            </ViewerProvider>
                          </LocalizationProvider>
                        </ThemeProvider>
                      </SettingsProvider>
                    </Router>
                  </ApolloProviderInit>
                </SocketProvider>
              </FeatureFlagProvider>
            </AuthKitProvider>
          </ConfigProvider>
        </ErrorBoundary>
      </StyledEngineProvider>
    </ErrorBoundary>,
  );
})();
